import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Banner,
  Box,
  Button,
  Icon,
  Modal,
  Spinner,
  Text,
} from "@shopify/polaris";
import {
  CashDollarIcon,
  CheckCircleIcon,
  ProductIcon,
  ShieldCheckMarkIcon,
  StarFilledIcon,
} from "@shopify/polaris-icons";

const planHeaderClass = {
  Lite: "plan-card__header--lite",
  Pro: "plan-card__header--pro",
  Unlimited: "plan-card__header--unlimited",
};

function PlanCard() {
  const [activePlanId, setActivePlanId] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [request, setRequest] = useState(true);
  const [loader, setLoader] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [warnings, setWarnings] = useState({
    disabled: false,
    message: "",
    tone: "warning",
  });
  const [cancelPlanModal, setCancelPlanModal] = useState(false);

  useEffect(() => {
    const fetchPlansAndStatus = async () => {
      try {
        const shop = new URLSearchParams(window.location.search).get("shop");

        const [plansResponse, statusResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/plans`),
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/shopify/plan?shop=${shop}`,
          ),
        ]);

        setPlans(plansResponse.data.plans || []);
        setActivePlanId(statusResponse.data.store?.plan_id || null);
        setActivePlan(statusResponse.data.activePlan || null);
      } catch (error) {
        console.error("Error fetching plans or plan status:", error);
        setWarnings({
          disabled: true,
          message: "Error fetching plans or plan status",
          tone: "critical",
        });
      } finally {
        setRequest(false);
      }
    };

    fetchPlansAndStatus();
  }, []);

  const handlePurchase = async (plan) => {
    setLoader(true);
    setSelectedPlan(plan.id);
    const shop = new URLSearchParams(window.location.search).get("shop");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/shopify/plan/purchase?shop=${shop}`,
        {
          plan_id: plan.id,
          shop,
        },
      );

      if (response.data.url) {
        window.top.location.href = response.data.url;
        return;
      }
      setActivePlanId(plan.id);
      setActivePlan(response.data.activePlan || plan);
    } catch (error) {
      console.error("Error purchasing plan:", error);
      setWarnings({
        disabled: true,
        message:
          error.response?.status === 404
            ? "Store not found"
            : "Something went wrong while creating the charge",
        tone: "critical",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleCancelPlan = async () => {
    setCancelPlanModal(false);
    setLoader(true);
    const shop = new URLSearchParams(window.location.search).get("shop");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/shopify/plan/cancel?shop=${shop}`,
      );
      setActivePlanId(null);
      setActivePlan(null);
    } catch (error) {
      console.error("Error cancelling plan:", error);
      setWarnings({
        disabled: true,
        message: "Failed to cancel the plan",
        tone: "critical",
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <div>
      <div className="app-page-hero">
        <div>
          <div className="app-page-hero__eyebrow">
            <Icon source={CashDollarIcon} tone="base" />
            Billing
          </div>
          <h1 className="app-page-hero__title">Choose your plan</h1>
          <p className="app-page-hero__subtitle">
            Unlock Google Merchant sync, product checks, and multi-country
            feeds based on catalog size.
          </p>
        </div>
        <div className="app-page-hero__meta">
          {activePlan ? (
            <span className="app-chip app-chip--success">
              <Icon source={StarFilledIcon} tone="success" />
              Active: {activePlan.heading}
            </span>
          ) : (
            <span className="app-chip app-chip--warn">No active plan</span>
          )}
          <span className="app-chip app-chip--info">
            <Icon source={ProductIcon} tone="base" />
            Monthly Shopify billing
          </span>
        </div>
      </div>

      {warnings.disabled ? (
        <Box paddingBlockEnd="400">
          <Banner tone={warnings.tone}>{warnings.message}</Banner>
        </Box>
      ) : null}

      {request ? (
        <div className="app-surface-card">
          <div
            className="app-surface-card__body"
            style={{ textAlign: "center", padding: 40 }}
          >
            <Spinner accessibilityLabel="Loading plans" size="large" />
          </div>
        </div>
      ) : (
        <div className="plan-grid">
          {plans.map((plan) => {
            const isActive = activePlanId === plan.id;
            return (
              <div
                key={plan.id}
                className={`plan-card${isActive ? " is-active" : ""}`}
              >
                <div
                  className={`plan-card__header ${
                    planHeaderClass[plan.heading] || "plan-card__header--lite"
                  }`}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span className="app-chip">
                      <Icon source={ShieldCheckMarkIcon} tone="base" />
                      {plan.heading}
                    </span>
                    {isActive ? (
                      <span className="app-chip app-chip--success">Active</span>
                    ) : null}
                  </div>
                  <div className="plan-card__price">
                    <strong>${plan.price}</strong>
                    <span>USD/mo</span>
                  </div>
                  <Text as="p" tone="subdued">
                    {plan.title}
                  </Text>
                </div>

                <div className="plan-card__body">
                  {isActive ? (
                    <>
                      <Button disabled primary fullWidth size="large">
                        Active plan
                      </Button>
                      <Button
                        onClick={() => setCancelPlanModal(true)}
                        tone="critical"
                        fullWidth
                        size="large"
                      >
                        Cancel plan
                      </Button>
                    </>
                  ) : (
                    <Button
                      disabled={loader && selectedPlan === plan.id}
                      loading={loader && selectedPlan === plan.id}
                      onClick={() => handlePurchase(plan)}
                      primary
                      fullWidth
                      size="large"
                    >
                      Select plan
                    </Button>
                  )}

                  <Text variant="headingSm" as="h3">
                    Included
                  </Text>
                  {(plan.highlights || []).map((highlight) => (
                    <div key={highlight} className="plan-feature">
                      <span className="plan-feature__icon">
                        <Icon source={CheckCircleIcon} tone="success" />
                      </span>
                      <span className="plan-feature__text">{highlight}</span>
                    </div>
                  ))}
                  <div className="plan-feature">
                    <span className="plan-feature__icon">
                      <Icon source={ProductIcon} tone="base" />
                    </span>
                    <span className="plan-feature__text">
                      Product cap:{" "}
                      {plan.productLimit
                        ? plan.productLimit.toLocaleString()
                        : "Unlimited"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={cancelPlanModal}
        onClose={() => setCancelPlanModal(false)}
        title="Cancel current subscription?"
        primaryAction={{
          content: "Yes, cancel",
          onAction: handleCancelPlan,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: "Keep plan",
            onAction: () => setCancelPlanModal(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            The store will lose access to features attached to the current paid
            plan after cancellation.
          </Text>
        </Modal.Section>
      </Modal>
    </div>
  );
}

export default PlanCard;
